/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as dateFns from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
/**
 * Returns UTC time shifted according to provided timezone
 *
 * @param timeZone Timezone name
 * @param now actual time
 * @returns local time shifted to UTC according to provided timezone
 */
export function zonedTime(timeZone, now = new Date()) {
    const zt = utcToZonedTime(now, timeZone);
    return zt;
}
export function today(now = new Date()) {
    return new Date(+dateFns.startOfDay(now) - now.getTimezoneOffset() * 60 * 1000);
}
export function addDays(dt, days) {
    return dateFns.addDays(dt, days);
}
/**
 * Returns parsed date or nul if date parsing fails
 * @param dateOrStr date or iso string to parse
 * @returns parsed date or null if date parsing fails
 */
export function parseDate(dateOrStr) {
    if (!dateOrStr) {
        return null;
    }
    const maybeDt = dateOrStr;
    const isDate = typeof maybeDt.getMonth === 'function';
    if (isDate) {
        return maybeDt;
    }
    const str = dateOrStr.toString();
    // `dd.MM.yyyy`: what the booth stored for calendar exceptions and provider
    // messages until it was fixed to send ISO. Still in Redis, so still read.
    const legacy = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(str);
    if (legacy) {
        const [, d, m, y] = legacy.map(Number);
        const dt = new Date(y, m - 1, d);
        return dateFns.isValid(dt) && dt.getDate() === d && dt.getMonth() === m - 1 ? dt : null;
    }
    const dt = dateFns.parseISO(str);
    return dateFns.isValid(dt) ? dt : null;
}
export function toISOString(dt) {
    if (!dt) {
        return null;
    }
    return dt.toISOString();
}
const PARSE_TIME_FORMATS = ['HH:mm', 'H:m', 'H:', 'H'];
export function parseTimeString(timeStr) {
    const refDate = Date.UTC(1900, 0, 1);
    for (const timeFormat of PARSE_TIME_FORMATS) {
        const resultHHmm = dateFns.parse(timeStr, timeFormat, refDate);
        if (dateFns.isValid(resultHHmm)) {
            const result = new Date(resultHHmm);
            return result;
        }
    }
    return null;
}
export function normalizeTime(timeStr) {
    const parsedTime = parseTimeString(timeStr);
    if (!parsedTime) {
        return null;
    }
    return dateFns.format(parsedTime, 'HH:mm');
}
export function isoDate(dt) {
    return dateFns.format(dt, 'yyyy-MM-dd');
}
export function isTodayOrFutureDay(dateOrStr, now = new Date()) {
    if (!dateOrStr) {
        return false;
    }
    const today = dateFns.startOfDay(now);
    const dt = parseDate(dateOrStr);
    if (!dt || !now) {
        return false;
    }
    const dtSoD = dateFns.startOfDay(dt);
    return dateFns.isAfter(dtSoD, today) || dateFns.isSameDay(dtSoD, today);
}
export function isSameMonth(dateOrStr, now = new Date()) {
    const dt = parseDate(dateOrStr);
    if (!dt || !now) {
        return false;
    }
    return dateFns.isSameMonth(dt, now);
}
export function isSameDay(dateOrStr, now = new Date()) {
    const dt = parseDate(dateOrStr);
    if (!dt || !now) {
        return false;
    }
    return dateFns.isSameDay(dt, now);
}
/**
 * Returns absolute difference in whole minutes between two dates
 * @param dt1 First Date or ISO String
 * @param dt2 Second Date or ISO String
 * @returns Absolute difference in minutes
 */
export function diffInMins(dt1, dt2 = new Date()) {
    return Math.round(diffInSeconds(dt1, dt2) / 60);
}
/**
 * Returns absolute difference in seconds between two dates
 * @param dt1 First Date or ISO String
 * @param dt2 Second Date or ISO String
 * @returns Absolute difference in minutes
 */
export function diffInSeconds(dt1, dt2 = new Date()) {
    const date1 = dt1;
    const date2 = dt2;
    // @ts-ignore
    const finalDt1 = date1.toISOString ? date1 : dateFns.parseISO(dt1);
    // @ts-ignore
    const finalDt2 = date2.toISOString ? date2 : dateFns.parseISO(dt2);
    return Math.abs(dateFns.differenceInSeconds(finalDt1, finalDt2));
}
export function isWithinRange(now, fromDt, toDt) {
    if (!now || !fromDt || !toDt) {
        return false;
    }
    try {
        return dateFns.isWithinInterval(now, { start: fromDt, end: toDt });
    }
    catch (e) {
        return false;
    }
}
function format(dateOrStr, fmt) {
    const dt = parseDate(dateOrStr);
    if (!dt) {
        return '';
    }
    return dateFns.format(dt, fmt);
}
export const dateFormats = {
    YYYYMMdd: (dt) => format(dt, 'yyyyMMdd'),
    isoDate: (dt) => format(dt, 'yyyy-MM-dd'),
    isoHHMM: (dt) => format(dt, 'HH:mm'),
    long: {
        timestamp: (dt) => format(dt, 'dd.MM.yyyy HH:mm:ss.SSS'),
        timestampNoDate: (dt) => format(dt, 'HH:mm:ss.SSS')
    },
    short: {
        date: (dt) => format(dt, 'd.M.yyyy'),
        time: (dt) => format(dt, 'HH:mm:ss'),
        dateTime: (dt) => format(dt, 'd.M.yyyy HH:mm:ss'),
        timestampNoDate: (dt) => format(dt, 'HH:mm:ss.SSS')
    }
};
export function timeStringToDate(now, timeString) {
    return dateFns.parseISO(`${dateFns.format(now, 'yyyy-MM-dd')}T${timeString}`);
}
//# sourceMappingURL=date-utils.js.map